import React, { useEffect, useState } from "react";
import { Scissors, SplitSquareHorizontal, Loader2 } from "lucide-react";
import GenerateButton from "@/components/GenerateButton";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { tagOf } from "@/lib/useCpSession";

const runJob = async (url, body) => {
  const { data } = await api.post(url, body);
  for (let n = 0; n < 1200; n++) {
    await new Promise((r) => setTimeout(r, n < 5 ? 1500 : 3000));
    const { data: j } = await api.get(`/bfx/cleanplate/jobs/${data.job_id}`);
    if (j.status === "done") return j.result;
    if (j.status === "failed") throw new Error(j.error || "Failed");
  }
  throw new Error("Took too long");
};
const fail = (e, fb) => e?.response ? errMsg(e, fb) : e?.message || fb;

export const plateFrame = (p) => { const m = /_(\d+)_(?:cleanplate|actionplate)(?:_v\d+)?$/.exec(tagOf(p)); return m ? Number(m[1]) : null; };
export const platesFor = (plates, part) => plates.filter((p) => { const f = plateFrame(p); return f == null || (f >= part.start && f <= part.end); });

// Number box that lets you clear and retype; clamps on blur / Enter.
export const FrameInput = ({ value, max, onCommit, testid, className, min = 0 }) => {
  const [txt, setTxt] = useState(String(value));
  useEffect(() => setTxt(String(value)), [value]);
  const commit = () => { const n = txt === "" ? value : Math.max(min, Math.min(max, Math.round(Number(txt)))); setTxt(String(n)); if (n !== value) onCommit(n); };
  return <input type="text" inputMode="numeric" value={txt} onChange={(e) => setTxt(e.target.value.replace(/[^\d]/g, ""))} onBlur={commit}
    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} data-testid={testid} className={className} />;
};

export const TrimBox = ({ projectId, src, info, onTrimmed }) => {
  const [a, setA] = useState(0);
  const [b, setB] = useState(Math.max(0, (info.frames || 1) - 1));
  const [busy, setBusy] = useState(false);
  const last = (info.frames || 1) - 1;
  const go = async () => {
    setBusy(true);
    try {
      const data = await runJob("/bfx/cleanplate/trim", { project_id: projectId, reference_id: src.id, start: a, end: b });
      onTrimmed(data);
      toast.success(`Trimmed to frames ${a}–${b} (${b - a + 1} frames)`);
    } catch (e) { toast.error(fail(e, "Could not trim the video")); } finally { setBusy(false); }
  };
  return (
    <details className="rounded-lg border border-zinc-800 p-2.5" data-testid="cp-trim">
      <summary className="text-xs text-zinc-300 cursor-pointer flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-[#41a7f5]" />Trim source video</summary>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {[["Start frame", a, setA], ["End frame", b, setB]].map(([l, v, set]) => (
          <label key={l} className="text-[10px] text-zinc-400">{l}
            <FrameInput value={v} max={last} onCommit={(n) => set(n)}
              className="ax-input bfx-input !h-9 text-xs mt-1 w-full" testid={`cp-trim-${l.split(" ")[0].toLowerCase()}`} />
          </label>
        ))}
      </div>
      <div className="text-[10px] font-mono text-zinc-500 mt-1.5">{b >= a ? `${b - a + 1} frames · ${((b - a + 1) / info.fps).toFixed(2)} s @ ${info.fps} fps` : "End must be after start"}</div>
      <div className="text-[11px] text-amber-300 mt-1.5" data-testid="cp-trim-notice">Trimming will take a little time, please wait.</div>
      <button type="button" onClick={go} disabled={busy || b <= a || (a === 0 && b === last)} data-testid="cp-trim-apply"
        className="mt-2 w-full h-9 rounded-lg border border-[#1477d2]/60 text-xs text-white flex items-center justify-center gap-1.5 hover:bg-[#0747a1]/30 disabled:opacity-40">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />}{busy ? "Trimming…" : "Trim & use as source"}</button>
    </details>
  );
};

export const PartsPanel = ({ projectId, src, info, st, patch, plates, onRun, running, minSecs = 5 }) => {
  const parts = st.parts?.source_id === src.id ? st.parts.list : null;
  if (!parts) return null;
  return (
    <div className="rounded-lg border border-zinc-800 p-2.5 space-y-2" data-testid="cp-parts">
      <div className="flex items-center gap-2">
        <SplitSquareHorizontal className="w-3.5 h-3.5 text-[#41a7f5]" />
        <span className="text-xs text-zinc-300 flex-1">Parts · {parts.length}</span>
      </div>
      {parts && (
        <>
          <div className="space-y-1">
            {parts.map((p) => {
              const n = platesFor(plates, p).length;
              return (
                <div key={p.index} className="flex items-center gap-2 text-[10px] font-mono rounded border border-zinc-800/70 bg-[#0B0F16] px-2 py-1" data-testid={`cp-part-${p.index}`}>
                  <span className="text-white">Part {p.index}</span>
                  <span className="text-zinc-500">f{p.start}–{p.end} · {((p.end - p.start + 1) / info.fps).toFixed(1)} s</span>
                  <span className={`ml-auto ${n ? "text-[#64b7f6]" : "text-amber-300"}`}>{n} plate{n === 1 ? "" : "s"}</span>
                </div>
              );
            })}
          </div>
          <GenerateButton onClick={() => onRun(parts)} busy={running} label={`Generate all ${parts.length} parts`} confirm confirmNote={`${parts.length} video generations will start.`}
            testid="cp-generate-parts" className="w-full bfx-generate" />
          <p className="text-[10px] text-zinc-500">Parts are queued and run one after another, a few at a time as slots free up. Use Stitch & Export above History to join them.</p>
        </>
      )}
    </div>
  );
};
